import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { AxiosMethods } from '@setvi/shared/enums';
import { QuoteConfigurationDTO } from 'pages/quotes/hooks/use-quote-config';
import { totalQuoteItems } from 'pages/quotes/quote-list/table/use-columns/total-quote-items';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';
import { PricingType } from 'pages/products/api';
import {
  QuoteItemDTO,
  QuoteSectionDTO,
  QuoteProductDTO,
  QuoteDTO
} from '../types';
import { extractItems } from '../components/quote-summary';

interface AutoSaveProps {
  delay?: number;
  onSuccess?: () => void;
}

export const getProductsPrices = async (body: any) =>
  fetch(`${process.env.REVOPS_API}/pricing/get`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`
    },
    method: AxiosMethods.POST,
    body: JSON.stringify(body)
  });

const isNumber = (value: string | number): boolean => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  const numericValue = Number(value);
  return !Number.isNaN(numericValue) && Number.isFinite(numericValue);
};

export const getMOQ = (item: QuoteProductDTO) => ({
  minimumQuoteQuantity: item.minimumQuoteQuantity ?? 1,
  overrideMinimumQuoteQuantity: item.overrideMinimumQuoteQuantity ?? true
});

const initializeProduct = (
  parentID: string | null,
  product: QuoteProductDTO
): Partial<QuoteProductDTO> => ({
  id: product.id || uuidv4(),
  _id: product._id,
  sell: product.sell,
  cost: product.cost,
  totalGrossProfit: product.totalGrossProfit || 0,
  totalGrossProfitPercentage: product.totalGrossProfitPercentage || 0,
  isPricingFetching: !product?.isFreeHand,
  isEditable: product?.isEditable,
  images: product.images,
  imageUrl: product.imageUrl,
  sku: product.sku,
  name: product.name,
  vendor: product.vendor,
  children: [],
  note: product.note || null,
  type: 'product',
  quantity:
    product?.overrideMinimumQuoteQuantity === false
      ? product?.minimumQuoteQuantity || 1
      : 1,
  parentID,
  productId: product._id,
  ...(product?.unitOfMeasure && { unitOfMeasure: product.unitOfMeasure }),
  ...(product?.isFreeHand && { isFreeHand: true }),
  ...getMOQ(product),
  rfqItemId: product.rfqItemId || undefined
});

interface QuoteStore {
  quote: QuoteDTO | null;
  quoteConfig: QuoteConfigurationDTO | null;
  autoSaveEnabled: boolean;
  lastSavedAt: Date | null;
  isSaving: boolean;

  onAddItems: (newItems: QuoteProductDTO[]) => Promise<void>;
  setAutoSaveEnabled: (enabled: boolean) => void;
  triggerAutoSave: (props?: AutoSaveProps) => void;

  addItems: (parentID: string | null, items: QuoteItemDTO[]) => void;
  replaceItem: (rfqItemId: string, newItem: QuoteProductDTO) => void;
  removeItem: (itemID: string) => void;
  moveItem: (
    fromIndex: number,
    toIndex: number,
    parentID: string | null
  ) => void;
  setQuote: (quote: QuoteDTO) => void;
  setQuoteConfig: (quoteConfig: QuoteConfigurationDTO) => void;
  resetQuote: () => void;
  addNoteToItem: (itemID: string, note: string) => void;
  updateQuoteInfo: (data: Partial<Record<keyof QuoteDTO, any>>) => void;
  updateSectionInfo: (
    itemId: string,
    value: { name?: string; note?: string }
  ) => void;
  updateQuoteItemById: (itemId: string, updates: Partial<QuoteItemDTO>) => void;
  updatePricing: (
    itemID: string,
    updates: Partial<{
      quantity: number;
      sell: number | string;
      totalGrossProfit: number | string;
      totalGrossProfitPercentage: number | string;
    }>
  ) => void;

  getTotalItems: () => number;
  getTotalUnits: () => number;
  getTotalCost: () => number;
  getTotalSell: () => number;
}

// Debounce utility
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const useQuoteStore = create<QuoteStore>()(
  persist(
    (set, get) => {
      // Auto-save function
      const autoSave = ({ delay = 2000, onSuccess }: AutoSaveProps = {}) =>
        debounce(async () => {
          const { quote, autoSaveEnabled } = get();

          if (!quote?._id || !autoSaveEnabled) return;

          set({ isSaving: true });

          try {
            const response = await fetch(
              `${process.env.REVOPS_API}/quote/${quote._id}/edit-quote`,
              {
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                method: AxiosMethods.PATCH,
                body: JSON.stringify({
                  name: quote.name,
                  description: quote.description || '',
                  opportunity: quote.opportunity || '',
                  customerNote: quote.customerNote || '',
                  internalNote: quote.internalNote || '',
                  prospect: quote.prospect,
                  items: extractItems(quote?.items),
                  ...(quote.validUntil && {
                    validUntil: moment
                      .utc(quote.validUntil)
                      .format('YYYY-MM-DD')
                  })
                })
              }
            );

            if (response.ok) {
              set({ lastSavedAt: new Date(), isSaving: false });
              onSuccess?.();
            } else {
              set({ isSaving: false });
            }
          } catch (error) {
            set({ isSaving: false });
            enqueueSnackbar('Error occurred while saving quote.', {
              variant: 'error'
            });
          }
        }, delay)(); // 2 second delay

      return {
        quote: null as QuoteDTO | null,
        quoteConfig: null as QuoteConfigurationDTO | null,
        autoSaveEnabled: true,
        lastSavedAt: null as Date | null,
        isSaving: false,

        setAutoSaveEnabled: (enabled: boolean) =>
          set({ autoSaveEnabled: enabled }),
        triggerAutoSave: (props: AutoSaveProps = {}) => autoSave(props),

        onAddItems: async (newItems: QuoteProductDTO[]) => {
          try {
            const accountNumber = get().quote?.account?.accountNumber;
            const segmentId = get().quote?.segment?._id;

            const response = await getProductsPrices({
              accountNumber: accountNumber ?? segmentId,
              products: newItems.map((product: QuoteProductDTO) => ({
                sku: product?.sku,
                quantity: 1
              })),
              type: PricingType.Quote
            });
            if (!response.ok) {
              throw new Error('Failed to fetch pricing data');
            }
            const data = await response.json();
            const pricing = data?.pricing;

            const updateItemPricing = (list: any[]): any[] =>
              list.map(item => {
                const shouldUpdatePricing = Boolean(item.isPricingFetching);

                const updatedProduct =
                  shouldUpdatePricing &&
                  pricing.find((p: any) => p.sku === item.sku);

                let updatedItem = item;

                if (shouldUpdatePricing) {
                  if (updatedProduct) {
                    updatedItem = {
                      ...item,
                      isEditable: get().quoteConfig?.enableEditingPrice
                        ? updatedProduct.isEditable
                        : false,
                      isPricingFetching: false,
                      sell: updatedProduct.sell,
                      cost: updatedProduct.cost,
                      totalGrossProfit: (
                        updatedProduct.sell - updatedProduct.cost
                      ).toFixed(2),
                      totalGrossProfitPercentage: (updatedProduct.cost !== 0
                        ? ((updatedProduct.sell - updatedProduct.cost) /
                            updatedProduct.sell) *
                          100
                        : 0
                      ).toFixed(2),
                      minGpPercentage:
                        updatedProduct.minimumGP != null
                          ? (updatedProduct.minimumGP * 100).toFixed(2)
                          : '0.00',
                      maxGpPercentage:
                        updatedProduct.maximumGP != null
                          ? (updatedProduct.maximumGP * 100).toFixed(2)
                          : '99.99'
                    };
                  } else {
                    updatedItem = { ...item, isPricingFetching: false };
                  }
                }

                if (item.children?.length) {
                  updatedItem = {
                    ...updatedItem,
                    children: updateItemPricing(item.children)
                  };
                }

                return updatedItem;
              });

            set(prevState => {
              let items = prevState.quote ? [...prevState.quote.items] : [];
              items = updateItemPricing(items);
              return { quote: { ...prevState.quote, items } };
            });

            // Trigger auto-save after pricing update
            autoSave();
          } catch (error) {
            // console.error('Error fetching prices:', error);
          }
        },
        addItems: (parentID, newItems) => {
          set(prevState => {
            let items = prevState.quote ? [...prevState.quote.items] : [];
            const processedItems = newItems.map(item =>
              item?.type === undefined
                ? initializeProduct(parentID, item as QuoteProductDTO)
                : item
            );

            const updateChildren = (list: any[]): any[] =>
              list.map(item => {
                const sectionId = item.id || item._id;

                if (sectionId === parentID) {
                  return {
                    ...item,
                    children: [...(item.children ?? []), ...processedItems]
                  };
                }

                if (item.children?.length) {
                  return {
                    ...item,
                    children: updateChildren(item.children)
                  };
                }

                return item;
              });

            items = parentID
              ? updateChildren(items)
              : [...items, ...processedItems];

            return { quote: { ...prevState.quote, items } };
          });

          if (newItems[0]?.type === 'section') {
            return;
          }

          const filteredItems = newItems.filter(
            item => !('isFreeHand' in item) || !item.isFreeHand
          ) as QuoteProductDTO[];

          if (filteredItems.length > 0) {
            get().onAddItems(filteredItems);
          }

          // Trigger auto-save after adding items
          autoSave();
        },
        replaceItem: (rfqItemId, newItem) => {
          set(prevState => {
            if (!prevState.quote) {
              return prevState;
            }

            let items = [...prevState.quote.items];
            let itemFound = false;

            // Using map() to preserve the original order of items
            const replaceInList = (list: any[], depth = 0): any[] =>
              list.map(item => {
                const itemRfqId =
                  'rfqItemId' in item ? item.rfqItemId : undefined;

                // Check if this is the item to replace
                if (itemRfqId === rfqItemId) {
                  itemFound = true;
                  // Preserve user modifications and original position
                  const processedItem = initializeProduct(
                    item.parentID,
                    newItem
                  );
                  // Item stays at the same index position
                  const replacedItem = {
                    ...processedItem,
                    id: item.id, // Preserve original ID
                    quantity: item.quantity, // Preserve user's quantity
                    note: item.note, // Preserve user's note
                    parentID: item.parentID, // Preserve parent relationship
                    rfqItemId, // Ensure rfqItemId is preserved
                    // Preserve pricing if user has edited it
                    ...(item.isEditable && item.sell !== processedItem.sell
                      ? {
                          sell: item.sell,
                          totalGrossProfit: item.totalGrossProfit,
                          totalGrossProfitPercentage:
                            item.totalGrossProfitPercentage
                        }
                      : {})
                  };
                  return replacedItem;
                }

                // Recursively check children
                if (item.children?.length) {
                  return {
                    ...item,
                    children: replaceInList(item.children, depth + 1)
                  };
                }

                return item;
              });

            items = replaceInList(items);

            // Only update if item was found and replaced
            if (!itemFound) {
              return prevState;
            }

            return { quote: { ...prevState.quote, items } };
          });

          // Fetch pricing for the new item if needed
          if (!newItem.isFreeHand) {
            get().onAddItems([newItem]);
          }

          // Trigger auto-save after replacing item
          autoSave();
        },
        removeItem: id => {
          set(state => {
            let items = state.quote ? [...state.quote.items] : [];

            const removeFromList = (list: any[]): any[] =>
              list
                .filter(item => (item.id ?? item._id) !== id)
                .map(item => ({
                  ...item,
                  children: item.children ? removeFromList(item.children) : []
                }));

            items = removeFromList(items);

            return { quote: { ...state.quote, items } };
          });

          // Trigger auto-save after removing item
          autoSave();
        },
        moveItem: (fromIndex, toIndex, parentID) => {
          set(state => {
            const items = state.quote ? [...state.quote.items] : [];

            if (parentID) {
              const parentItem = items.find(
                item => (item.id ?? item._id) === parentID
              );
              if (parentItem) {
                if ('products' in parentItem) {
                  const section = parentItem as QuoteSectionDTO;
                  const movedProduct = section.children.splice(fromIndex, 1)[0];
                  section.children.splice(toIndex, 0, movedProduct);
                } else if ('children' in parentItem) {
                  const product = parentItem as QuoteProductDTO;
                  const movedSubProduct = product.children.splice(
                    fromIndex,
                    1
                  )[0];
                  product.children.splice(toIndex, 0, movedSubProduct);
                }
              }
            } else {
              const movedItem = items.splice(fromIndex, 1)[0];
              items.splice(toIndex, 0, movedItem);
            }

            return { quote: state.quote ? { ...state.quote, items } : null };
          });

          // Trigger auto-save after moving item
          autoSave();
        },
        setQuote: quote => set({ quote }),
        setQuoteConfig: (quoteConfig: any) => set({ quoteConfig }),
        resetQuote: () =>
          set({ quote: null, lastSavedAt: null, isSaving: false }),
        addNoteToItem: (id, note) => {
          set(state => {
            let items = state.quote ? [...state.quote.items] : [];

            const updateItemNote = (list: any[]): any[] =>
              list.map(item => ({
                ...item,
                note: (item.id ?? item._id) === id ? note : item.note,
                children: item.children ? updateItemNote(item.children) : []
              }));

            items = updateItemNote(items);

            return { quote: { ...state.quote, items } };
          });

          // Trigger auto-save after adding note
          autoSave();
        },
        updatePricing: (
          id: string,
          updates: Partial<{
            quantity: number;
            sell: number;
            totalGrossProfit: number;
            totalGrossProfitPercentage: number;
          }>
        ) => {
          set(state => {
            let items = state.quote ? [...state.quote.items] : [];

            const updateItemPricing = (list: any[]): any[] =>
              list.map(item => ({
                ...item,
                ...((item.id ?? item._id) === id ? updates : {}),
                children: item.children ? updateItemPricing(item.children) : []
              }));

            items = updateItemPricing(items);

            return { quote: { ...state.quote, items } };
          });

          // Trigger auto-save after updating pricing
          autoSave();
        },
        updateQuoteInfo: data => {
          set(state => ({
            quote: { ...state.quote, ...data }
          }));

          // Trigger auto-save after updating quote info
          autoSave();
        },
        updateSectionInfo: (
          id: string,
          value: { name?: string; note?: string }
        ) => {
          set(state => {
            let items = state.quote ? [...state.quote.items] : [];

            const updateSection = (list: any[]): any[] =>
              list.map(item => {
                if ((item.id ?? item._id) === id) {
                  return { ...item, ...value };
                }

                if (item.children?.length) {
                  return { ...item, children: updateSection(item.children) };
                }

                return item;
              });

            items = updateSection(items);

            return { quote: { ...state.quote, items } };
          });

          // Trigger auto-save after updating section info
          autoSave();
        },
        updateQuoteItemById: (id: string, updates: Partial<QuoteItemDTO>) => {
          set(state => {
            let items = state.quote ? [...state.quote.items] : [];

            const updateItem = (list: any[]): any[] =>
              list.map(item => {
                if ((item.id ?? item._id) === id) {
                  return { ...item, ...updates };
                }

                if (item.children?.length) {
                  return { ...item, children: updateItem(item.children) };
                }

                return item;
              });

            items = updateItem(items);

            return { quote: { ...state.quote, items } };
          });

          // Trigger auto-save after updating item
          autoSave();
        },
        getTotalItems: () => {
          const { quote } = get();
          if (!quote || !quote.items) return 0;

          return totalQuoteItems(quote.items);
        },
        getTotalUnits: () => {
          const { quote } = get();

          if (!quote || !quote.items) return 0;

          const countUnits = (items: QuoteItemDTO[]): number =>
            items.reduce((total, item) => {
              const quantity = 'quantity' in item ? (item.quantity ?? 1) : 0;
              let childUnits = 0;

              if ('products' in item && Array.isArray(item.products)) {
                childUnits = countUnits(item.products);
              } else if ('children' in item && Array.isArray(item.children)) {
                childUnits = countUnits(item.children);
              }

              return total + quantity + childUnits;
            }, 0);

          return countUnits(quote.items);
        },
        getTotalCost: () => {
          const { quote } = get();
          if (!quote || !quote.items) return 0;

          const calculateCost = (items: QuoteItemDTO[]): number =>
            items.reduce((total, item) => {
              if ('children' in item && Array.isArray(item.children)) {
                if (item.type === 'section') {
                  return total + calculateCost(item.children);
                }
              }

              if ('cost' in item && isNumber(item.cost)) {
                const itemQuantity =
                  'quantity' in item ? (item.quantity ?? 1) : 1;
                return total + item.cost * itemQuantity;
              }

              return total;
            }, 0);

          return calculateCost(quote.items);
        },
        getTotalSell: () => {
          const { quote } = get();
          if (!quote || !quote.items) return 0;

          const calculateSell = (items: QuoteItemDTO[]): number =>
            items.reduce((total, item) => {
              if ('children' in item && Array.isArray(item.children)) {
                if (item.type === 'section') {
                  return total + calculateSell(item.children);
                }
              }

              if ('sell' in item && isNumber(item.sell)) {
                const itemQuantity =
                  'quantity' in item ? (item.quantity ?? 1) : 1;
                return total + item.sell * itemQuantity;
              }

              return total;
            }, 0);

          return calculateSell(quote.items);
        }
      };
    },
    { name: 'quote-store' }
  )
);
